import type {
  AccountSnapshot,
  CommandName,
  DeliveryStatus,
  IntentRecord,
  PositionSnapshot,
} from "../../shared/contracts";
import { formatTaipeiDateTime } from "./time";

export function aggregateStatus(intent: IntentRecord): DeliveryStatus {
  const statuses = intent.deliveries.map((delivery) => delivery.status);
  if (!statuses.length || statuses.some((status) => status === "pending")) return "pending";
  if (statuses.some((status) => status === "claimed")) return "claimed";
  if (statuses.every((status) => status === "executed")) return "executed";
  if (statuses.some((status) => status === "failed")) return "failed";
  if (statuses.some((status) => status === "rejected")) return "rejected";
  return "expired";
}

export function statusName(status: DeliveryStatus): string {
  return ({
    pending: "等待 EA",
    claimed: "EA 處理中",
    executed: "已完成",
    rejected: "已拒絕",
    failed: "執行失敗",
    expired: "已過期",
  })[status];
}

export function strategyName(value: string): string {
  return ({
    fibo618: "Fibo進",
    market: "市價進",
    market_order: "市價成交",
    pending_order: "建立掛單",
    close_all: "平倉所有持倉",
    delete_pending: "刪除所有掛單",
    close_position: "單筆平倉",
    delete_order: "刪除掛單",
    set_take_profit: "設定全部停利",
  } as Record<string, string>)[value] ?? value;
}

export function orderTypeName(value: string): string {
  return ({
    buy: "buy",
    sell: "sell",
    buy_limit: "buy limit",
    sell_limit: "sell limit",
    buy_stop: "buy stop",
    sell_stop: "sell stop",
  } as Record<string, string>)[value] ?? value;
}

export function tradeModeBadge(value: AccountSnapshot["tradeMode"] | undefined): string {
  const mode = value ?? "unknown";
  const label = ({
    demo: "DEMO 模擬",
    real: "REAL 真實",
    contest: "CONTEST 競賽",
    unknown: "UNKNOWN 未知",
  } as const)[mode];
  return `<span class="trade-mode-badge ${mode}">${label}</span>`;
}

export function formatMoney(value: number, currency: string, digits: number): string {
  try {
    return new Intl.NumberFormat("zh-TW", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value);
  } catch {
    return `${value.toFixed(digits)} ${currency}`;
  }
}

export function formatPrice(value: number): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  }).format(value);
}

export function formatOptionalPrice(value: number | null | undefined): string {
  return value === null || value === undefined || value === 0 ? "—" : formatPrice(value);
}

export function formatOptionalNumber(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : value.toFixed(2);
}

export function formatLots(value: number): string {
  return value.toFixed(Math.max(2, value < 0.01 ? 3 : 2));
}

export function formatDateTime(value: number): string {
  return formatTaipeiDateTime(value);
}

export function formatTradeTime(value: number): string {
  return formatTaipeiDateTime(value).slice(0, -3);
}

export function relativeTime(value: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - value) / 1_000));
  if (seconds < 10) return "剛剛";
  if (seconds < 60) return `${seconds} 秒前`;
  const minutes = Math.round(seconds / 60);
  return minutes < 60 ? `${minutes} 分前` : formatDateTime(value);
}

export function sumProfit(items: PositionSnapshot[]): number {
  return items.reduce((sum, item) => sum + (item.profit ?? 0), 0);
}

export function shortId(value: string): string {
  return value.length > 12 ? `${value.slice(0, 8)}…${value.slice(-4)}` : value;
}

export function rowKey(accountId: string, ticket: number, command: CommandName): string {
  return `${accountId}:${ticket}:${command}`;
}

export function accountChevron(expanded: boolean): string {
  return `<svg class="chevron-icon${expanded ? " is-expanded" : ""}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 10l5 5 5-5"></path></svg>`;
}

export function menuDots(): string {
  return `<span class="menu-dots" aria-hidden="true"><i></i><i></i><i></i></span>`;
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  })[character]!);
}

export function escapeAttr(value: string): string {
  return escapeHtml(value);
}
